import React, { useState, useEffect } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { 
  Container, 
  Stack, 
  TextInput, 
  Button, 
  Title, 
  Alert,
  Paper,
  Text,
  Group,
  Loader,
  ScrollArea,
  Card,
  Badge,
  Box,
  Divider
} from '@mantine/core';
import { dbService } from "../firebaseInstance";
import { createCollection } from "../utils/pureDataFetch";
import UpdateCollectionModal from "../components/modal/UpdateCollectionModal";
import { useIsMobile } from "../hooks/useIsMobile";
import moment from "moment";

// 안정적인 빈 배열 참조
const EMPTY_ARRAY = [];

const AdminCreateCollection = () => {
  const [targetUserID, setTargetUserID] = useState("");
  const [targetUserProfile, setTargetUserProfile] = useState(null);
  const [lifetimeThreadsList, setLifetimeThreadsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // 컬렉션 생성 관련 state
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedThreads, setSelectedThreads] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  
  const isMobile = useIsMobile('xl');

  // 특정 유저의 threads 데이터 가져오기
  const fetchUserThreads = async (userID) => {
    setIsLoading(true);
    setError(null);
    setLifetimeThreadsList([]);
    setTargetUserProfile(null);
    
    try {
      // 1. 유저 프로필 가져오기
      const userDoc = await dbService.collection('user').doc(userID).get();
      if (!userDoc.exists) {
        throw new Error('해당 유저를 찾을 수 없습니다.');
      }
      
      const userData = userDoc.data();
      if (!userData.profile) {
        throw new Error('유저 프로필이 없습니다.');
      }
      
      setTargetUserProfile(userData.profile);
      
      // 2. threads 서브컬렉션에서 모든 threads 가져오기
      const threadsSnapshot = await dbService
        .collection('user')
        .doc(userID)
        .collection('threads')
        .get();
      
      const allThreads = [];
      threadsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data && Array.isArray(data.list)) {
          allThreads.push(...data.list);
        }
      });
      
      // 날짜순 정렬 (최신순)
      const sortedThreads = allThreads.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      
      setLifetimeThreadsList(sortedThreads);
      setSuccess(`${userData.profile.username}님의 ${sortedThreads.length}개 게시물을 불러왔습니다.`);
      
    } catch (err) {
      console.error("Error fetching user threads:", err);
      setError(err.message || "threads 데이터를 가져오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!targetUserID.trim()) {
      setError("User ID를 입력해주세요.");
      return;
    }
    fetchUserThreads(targetUserID.trim());
  };

  const handleThreadSelect = (threadId) => {
    setSelectedThreads((prev) =>
      prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId]
    );
  };

  useEffect(() => {
    setCollectionList(() => {
      const threadsList = lifetimeThreadsList || EMPTY_ARRAY;
      return threadsList
        .filter((thread) => selectedThreads.includes(thread.id))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
  }, [selectedThreads, lifetimeThreadsList]);

  const handleRemoveThread = (threadId) => {
    setSelectedThreads((prev) => prev.filter((id) => id !== threadId));
    setCollectionList((prev) =>
      prev.filter((thread) => thread.id !== threadId)
    );
  };

  const handleCreateCollection = async (collectionData) => {
    try {
      console.log("[AdminCreateCollection] Creating collection for user:", targetUserID);
      console.log("[AdminCreateCollection] Collection data:", collectionData);
      
      // 실제 유저의 ID로 컬렉션 생성 (대문자 ID 사용)
      const userCollectionData = {
        ...collectionData,
        userID: targetUserID,  // 대문자 ID로 변경 (실제 글 주인)
        createdAt: new Date().toISOString()
      };
      
      const newCollection = await createCollection(userCollectionData);
      console.log("[AdminCreateCollection] Collection created successfully:", newCollection);
      
      setSuccess(`${targetUserProfile.username}님의 컬렉션이 생성되었습니다!`);
      setModalOpen(false);
      
      // 상태 초기화
      setSelectedThreads([]);
      setCollectionList([]);
      setTitle("");
      setDescription("");
      
    } catch (error) {
      console.error("컬렉션 생성 오류:", error);
      setError("컬렉션 생성에 실패했습니다: " + error.message);
    }
  };

  const calculateMetrics = () => {
    if (!selectedThreads.length) return null;
    
    const selectedThreadData = lifetimeThreadsList.filter(t => 
      selectedThreads.includes(t.id)
    );
    
    const totalMetrics = selectedThreadData.reduce((acc, thread) => ({
      views: acc.views + (thread.insights?.views || 0),
      likes: acc.likes + (thread.insights?.likes || 0),
      replies: acc.replies + (thread.insights?.replies || 0),
      reposts: acc.reposts + (thread.insights?.reposts || 0),
      quotes: acc.quotes + (thread.insights?.quotes || 0)
    }), { views: 0, likes: 0, replies: 0, reposts: 0, quotes: 0 });
    
    return totalMetrics;
  };

  const metrics = calculateMetrics();

  return (
    <PageContainer>
      <Container size="lg" pt="lg">
        <Stack gap="lg">
          <Title order={2}>Admin - Collection 생성</Title>
          
          {/* User ID 입력 섹션 */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Title order={4}>유저 검색</Title>
              <Group>
                <TextInput
                  placeholder="User ID를 입력하세요 (예: 12345678)"
                  value={targetUserID}
                  onChange={(e) => setTargetUserID(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  disabled={isLoading}
                />
                <Button 
                  onClick={handleSearch} 
                  loading={isLoading}
                  disabled={!targetUserID.trim()}
                >
                  검색
                </Button>
              </Group>
              
              {error && <Alert color="red">{error}</Alert>}
              {success && <Alert color="green">{success}</Alert>}
            </Stack>
          </Paper>

          {/* 유저 프로필 표시 */}
          {targetUserProfile && (
            <Paper p="md" withBorder>
              <Group>
                <img
                  src={targetUserProfile.threads_profile_picture_url}
                  alt={targetUserProfile.username}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
                <div>
                  <Text size="lg" weight={600}>
                    @{targetUserProfile.username}
                  </Text>
                  <Text size="sm" color="dimmed">
                    {targetUserProfile.name} · ID: {targetUserProfile.id}
                  </Text>
                  <Text size="xs" color="dimmed">
                    게시물: {lifetimeThreadsList.length}개
                  </Text>
                </div>
              </Group>
            </Paper>
          )}

          {/* 선택된 threads 메트릭 */}
          {selectedThreads.length > 0 && metrics && (
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Title order={5}>선택된 게시물 통계</Title>
                <Group>
                  <Badge size="lg" variant="light">
                    게시물: {selectedThreads.length}개
                  </Badge>
                  <Badge size="lg" variant="light" color="blue">
                    조회수: {metrics.views.toLocaleString()}
                  </Badge>
                  <Badge size="lg" variant="light" color="pink">
                    좋아요: {metrics.likes.toLocaleString()}
                  </Badge>
                  <Badge size="lg" variant="light" color="green">
                    답글: {metrics.replies.toLocaleString()}
                  </Badge>
                  <Badge size="lg" variant="light" color="orange">
                    리포스트: {metrics.reposts.toLocaleString()}
                  </Badge>
                </Group>
                <Button 
                  onClick={() => setModalOpen(true)}
                  disabled={selectedThreads.length === 0}
                >
                  컬렉션 생성하기
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Threads 목록 */}
          {lifetimeThreadsList.length > 0 && (
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Title order={4}>게시물 목록</Title>
                <Text size="sm" color="dimmed">
                  컬렉션에 포함할 게시물을 선택하세요
                </Text>
                
                <ScrollArea h={600}>
                  <Stack gap="sm">
                    {lifetimeThreadsList.map((thread) => (
                      <Card 
                        key={thread.id} 
                        p="sm" 
                        withBorder
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedThreads.includes(thread.id) 
                            ? 'rgba(34, 139, 230, 0.1)' 
                            : 'transparent',
                          borderColor: selectedThreads.includes(thread.id)
                            ? '#228be6'
                            : undefined
                        }}
                        onClick={() => handleThreadSelect(thread.id)}
                      >
                        <Stack gap="xs">
                          <Group position="apart">
                            <Text size="xs" color="dimmed">
                              {moment(thread.timestamp).format("YYYY-MM-DD HH:mm")}
                            </Text>
                            {selectedThreads.includes(thread.id) && (
                              <Badge color="blue" size="sm">선택됨</Badge>
                            )}
                          </Group>
                          
                          <Text size="sm" lineClamp={3}>
                            {thread.text || '(내용 없음)'}
                          </Text>
                          
                          {thread.media_url && (
                            <img 
                              src={thread.media_url} 
                              alt=""
                              style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }}
                            />
                          )}
                          
                          <Group gap="xs">
                            <Badge size="xs" variant="dot">
                              조회 {thread.insights?.views || 0}
                            </Badge>
                            <Badge size="xs" variant="dot" color="pink">
                              좋아요 {thread.insights?.likes || 0}
                            </Badge>
                            <Badge size="xs" variant="dot" color="green">
                              답글 {thread.insights?.replies || 0}
                            </Badge>
                          </Group>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>

      {/* 컬렉션 생성 모달 */}
      {targetUserProfile && (
        <UpdateCollectionModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          threadsList={lifetimeThreadsList || EMPTY_ARRAY}
          selectedThreads={selectedThreads}
          handleThreadSelect={handleThreadSelect}
          isMobile={isMobile}
          onUpdateCollection={handleCreateCollection}
          collectionList={collectionList}
          handleRemoveThread={handleRemoveThread}
          isNew={true}
          overrideUserProfile={targetUserProfile}
        />
      )}
    </PageContainer>
  );
};

export default AdminCreateCollection;